import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useReaderFunnel } from '../contexts/ReaderFunnelContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { User, Mail, Shield, Phone, MapPin, Lock, Eye, EyeOff, Crown, CreditCard, Coins, Calendar, RefreshCw, ChevronDown, ChevronUp, Clock, CheckCircle2, XCircle, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import UniversalFooter from '../components/UniversalFooter';
import PricingConversionModal from '../components/PricingConversionModal';
import PurchaseConfirmationModal from '../components/PurchaseConfirmationModal';
import { BACKEND_URL } from '@/config/api';

// BACKEND_URL imported from centralized config
const API = `${BACKEND_URL}/api`;

const AccountPage = () => {
  const { user, logout, getHomePage, loadCurrentUser } = useAuth();
  const { setShowPricingModal } = useReaderFunnel();
  const navigate = useNavigate();
  
  // Membership data state
  const [membershipData, setMembershipData] = useState(null);
  const [loadingMembership, setLoadingMembership] = useState(true);
  
  // Transaction history state
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [transactionsExpanded, setTransactionsExpanded] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [transactionTotal, setTransactionTotal] = useState(0);
  
  // Generate last 12 months for filter
  const getMonthOptions = () => {
    const options = [{ value: 'all', label: 'All Time' }];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      options.push({ value, label });
    }
    return options;
  };
  
  // Fetch transactions
  const fetchTransactions = useCallback(async () => {
    if (!user?.id) return;
    
    setLoadingTransactions(true);
    
    try {
      const monthParam = selectedMonth !== 'all' ? `&month=${selectedMonth}` : '';
      const response = await fetch(
        `${API}/nfm/reader/transactions?user_id=${user.id}${monthParam}&limit=50`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions || []);
        setTransactionTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
    } finally {
      setLoadingTransactions(false);
    }
  }, [user?.id, selectedMonth]);
  
  // Load transactions when expanded or month changes
  useEffect(() => {
    if (transactionsExpanded) {
      fetchTransactions();
    }
  }, [transactionsExpanded, fetchTransactions]);
  
  // Transaction helper functions
  const getStatusIcon = (status) => {
    switch (status) {
      case 'settled':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-amber-500" />;
      case 'failed':
      case 'refunded':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-slate-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'settled': return 'Completed';
      case 'pending': return 'Pending';
      case 'failed': return 'Failed';
      case 'refunded': return 'Refunded';
      default: return status;
    }
  };

  const formatTransactionDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTransactionAmount = (cents, type) => {
    const dollars = (cents / 100).toFixed(2);
    const isRefund = type === 'Refund' || type === 'Adjustment';
    return (
      <span className={isRefund ? 'text-red-400' : 'text-white'}>
        {isRefund ? '-' : ''}${dollars}
      </span>
    );
  };
  
  // Fetch membership data on mount
  useEffect(() => {
    const fetchMembershipData = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) return;
        
        const response = await axios.get(`${API}/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setMembershipData(response.data);
      } catch (error) {
        console.error('Error fetching membership data:', error);
      } finally {
        setLoadingMembership(false);
      }
    };
    
    fetchMembershipData();
  }, []);
  
  // Get role-specific home label
  const getHomeLabel = () => {
    if (!user) return "Home";
    
    const roles = user?.roles || [];
    
    // Check for author role (handles both "author" and legacy "creator")
    const hasAuthorRole = roles.includes('author') || roles.includes('creator');
    
    if (roles.includes('admin')) {
      return "Admin Home";
    } else if (hasAuthorRole) {
      return "Author Home";
    } else {
      return "Reader Home";
    }
  };
  
  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return '—';
    }
  };
  
  // Handle buy credits click
  const handleBuyCredits = () => {
    setShowPricingModal(true);
  };
  
  // Form state
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    alternate_email: user?.alternate_email || '',
    city: user?.city || '',
    state: user?.state || '',
    country: user?.country || ''
  });
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSaveAndClose = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        toast.error('Not authenticated. Please log in again.');
        navigate('/login');
        return;
      }

      // Update profile
      await axios.put(
        `${API}/auth/update-profile`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // If changing password, handle that separately
      if (changingPassword) {
        if (passwordData.new_password !== passwordData.confirm_password) {
          toast.error('New passwords do not match');
          setSaving(false);
          return;
        }
        
        if (passwordData.new_password.length < 8) {
          toast.error('Password must be at least 8 characters');
          setSaving(false);
          return;
        }

        await axios.post(
          `${API}/auth/change-password`,
          {
            current_password: passwordData.current_password,
            new_password: passwordData.new_password
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      toast.success('Profile updated successfully');
      
      // Navigate back to user's homepage
      navigate(getHomePage(user));
      
    } catch (error) {
      console.error('Error updating profile:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to update profile';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      {/* Top Navigation Bar */}
      <nav className="sticky top-0 z-50 border-b border-slate-800 bg-slate-950/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to={getHomePage(user)} className="flex items-center gap-2 group">
            <img 
              src="/newaudia-logo.jpg" 
              alt="NewAudia™" 
              className="h-16 w-32 object-contain rounded transition-transform group-hover:scale-105"
            />
            <span className="text-sm font-semibold text-slate-50">
              NewAudia<sup className="font-bold text-[0.3em] text-white">TM</sup>
            </span>
          </Link>

          <div className="flex items-center gap-3 text-xs sm:text-sm">
            <Link
              to={getHomePage(user)}
              className="rounded-full px-3 py-1 text-slate-300 hover:bg-slate-800 transition-colors"
            >
              {getHomeLabel()}
            </Link>
            <button
              onClick={handleLogout}
              className="rounded-full bg-slate-800 px-3 py-1 text-slate-100 hover:bg-slate-700 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="mx-auto max-w-4xl px-4 py-8 sm:py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold">My Account</h1>
          <p className="mt-2 text-slate-400">
            Manage your NewAudia<sup className="font-bold text-[0.6em]">TM</sup> profile and preferences
          </p>
        </div>

        {/* Profile Card */}
        <div className="rounded-2xl bg-slate-900/60 p-6 shadow-xl ring-1 ring-slate-800 space-y-6">
          <div className="flex items-center gap-4 pb-6 border-b border-slate-800">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-indigo-500 text-white text-2xl font-bold">
              {(formData.first_name?.[0] || user?.name?.[0] || 'U').toUpperCase()}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-xl font-semibold">
                  {formData.first_name && formData.last_name 
                    ? `${formData.first_name} ${formData.last_name}`
                    : user?.name || 'User'}
                </h2>
                {/* Membership Badge */}
                {membershipData && (
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                    membershipData.membership_type === 'Subscriber'
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                      : membershipData.membership_type === 'ALaCarte'
                        ? 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-400/40'
                        : 'bg-slate-700 text-slate-300'
                  }`}>
                    {membershipData.membership_type === 'Subscriber' && <Crown className="w-3 h-3" />}
                    {membershipData.membership_type === 'ALaCarte' && <CreditCard className="w-3 h-3" />}
                    {membershipData.membership_type === 'Subscriber' 
                      ? `Subscriber${membershipData.subscription?.tier_code ? ` • ${membershipData.subscription.tier_code.charAt(0).toUpperCase() + membershipData.subscription.tier_code.slice(1)}` : ''}`
                      : membershipData.membership_type === 'ALaCarte'
                        ? 'A La Carte'
                        : 'Free'}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-400">{user?.email}</p>
            </div>
          </div>

          {/* Membership & Credits Section */}
          {membershipData && (
            <div className="pb-6 border-b border-slate-800">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Crown className="w-5 h-5 text-purple-400" />
                Membership & Credits
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Current Balance Card */}
                <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <Coins className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Reader Credits</p>
                      <p className="text-2xl font-bold text-amber-400">
                        {membershipData.credits_balance?.toFixed(2) || '0.00'} RC
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handleBuyCredits}
                    size="sm"
                    className="w-full bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 text-slate-900 font-semibold"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Buy More Credits
                  </Button>
                </div>

                {/* Subscription Info Card (for Subscribers) */}
                {membershipData.membership_type === 'Subscriber' && membershipData.subscription && (
                  <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 rounded-xl p-4 border border-purple-500/30">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">
                          {membershipData.subscription.billing_cycle === 'annual' ? 'Annual' : 'Monthly'} Subscription
                        </p>
                        <p className="text-sm font-semibold text-white">
                          {membershipData.subscription.tier_code?.charAt(0).toUpperCase() + membershipData.subscription.tier_code?.slice(1)} Plan
                        </p>
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      {/* Monthly Credits */}
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400 flex items-center gap-1.5">
                          <Coins className="w-3.5 h-3.5" />
                          Monthly Credits
                        </span>
                        <span className="text-purple-300 font-medium">
                          {membershipData.subscription.monthly_credits || 0} RC
                        </span>
                      </div>
                      
                      {/* Next Credit Top-off Date (for annual) */}
                      {membershipData.subscription.billing_cycle === 'annual' && membershipData.subscription.next_credit_date && (
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400 flex items-center gap-1.5">
                            <RefreshCw className="w-3.5 h-3.5" />
                            Next Credit Top-off
                          </span>
                          <span className="text-green-400 font-medium">
                            {formatDate(membershipData.subscription.next_credit_date)}
                          </span>
                        </div>
                      )}
                      
                      {/* Renewal Date */}
                      {membershipData.subscription.next_renewal_date && (
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400 flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" />
                            Renewal Date
                          </span>
                          <span className="text-slate-300">
                            {formatDate(membershipData.subscription.next_renewal_date)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* A La Carte Info Card */}
                {membershipData.membership_type === 'ALaCarte' && (
                  <div className="bg-amber-900/20 rounded-xl p-4 border border-amber-500/30">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">A La Carte</p>
                        <p className="text-sm font-semibold text-white">Pay As You Go</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-400">
                      Purchase credit bundles anytime. No monthly commitment required.
                    </p>
                  </div>
                )}
                
                {/* Free Tier Info Card */}
                {membershipData.membership_type === 'Free' && (
                  <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                        <User className="w-5 h-5 text-slate-400" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Free Account</p>
                        <p className="text-sm font-semibold text-white">Complimentary Credits</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mb-3">
                      Upgrade to a subscription for monthly credits and exclusive benefits.
                    </p>
                    <Button
                      onClick={handleBuyCredits}
                      size="sm"
                      variant="outline"
                      className="w-full border-purple-500/50 text-purple-300 hover:bg-purple-500/20"
                    >
                      View Plans
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Subscription & Billing Link */}
              <div className="mt-4 pt-4 border-t border-slate-700/50">
                <Link 
                  to="/account/subscription"
                  className="inline-flex items-center gap-2 text-sm text-amber-400 hover:text-amber-300 transition-colors"
                  data-testid="subscription-billing-link"
                >
                  <CreditCard className="w-4 h-4" />
                  Manage Subscription & Billing
                  <span className="text-slate-500">→</span>
                </Link>
              </div>
            </div>
          )}

          {/* Transaction History Section */}
          <div className="pb-6 border-b border-slate-800">
            <button
              onClick={() => setTransactionsExpanded(!transactionsExpanded)}
              className="w-full flex items-center justify-between text-lg font-semibold hover:text-slate-200 transition-colors"
              data-testid="transactions-toggle"
            >
              <span className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-purple-400" />
                Transaction History
              </span>
              {transactionsExpanded ? (
                <ChevronUp className="w-5 h-5 text-slate-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-slate-400" />
              )}
            </button>
            
            {transactionsExpanded && (
              <div className="mt-4 space-y-4">
                {/* Filter */}
                <div className="flex items-center gap-4 bg-slate-800/50 rounded-xl p-3">
                  <Calendar className="h-5 w-5 text-slate-400" />
                  <Select
                    value={selectedMonth}
                    onValueChange={setSelectedMonth}
                  >
                    <SelectTrigger className="w-[200px] bg-slate-800 border-slate-700 text-slate-100" data-testid="month-filter">
                      <SelectValue placeholder="Select month" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {getMonthOptions().map(option => (
                        <SelectItem 
                          key={option.value} 
                          value={option.value}
                          className="text-slate-100 hover:bg-slate-700 focus:bg-slate-700"
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-slate-400">
                    {transactionTotal} transaction{transactionTotal !== 1 ? 's' : ''}
                  </span>
                </div>
                
                {/* Transactions List */}
                <div className="bg-slate-800/30 rounded-xl border border-slate-700/50">
                  {loadingTransactions ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                    </div>
                  ) : transactions.length === 0 ? (
                    <div className="text-center py-12">
                      <Receipt className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                      <p className="text-slate-400">No transactions found</p>
                      <p className="text-sm text-slate-500 mt-2">
                        Your payment history will appear here
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-700/50">
                      {transactions.map((txn) => (
                        <div
                          key={txn.transaction_id || txn.txn_id}
                          className="py-4 px-4 flex items-center justify-between hover:bg-slate-800/50 transition-colors"
                          data-testid={`transaction-${txn.transaction_id || txn.txn_id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-500/20">
                              <CreditCard className="h-5 w-5 text-purple-400" />
                            </div>
                            <div>
                              <p className="font-medium text-white">{txn.product_name || txn.product_id}</p>
                              <p className="text-sm text-slate-400">{txn.type}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">
                              {formatTransactionAmount(txn.amount_cents, txn.type)}
                            </p>
                            <div className="flex items-center gap-1 justify-end text-sm text-slate-400">
                              {getStatusIcon(txn.status)}
                              <span>{getStatusText(txn.status)}</span>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              {formatTransactionDate(txn.created_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Summary */}
                {transactions.length > 0 && (
                  <div className="bg-slate-800/30 rounded-xl p-4 border border-slate-700/50 flex justify-between items-center">
                    <span className="text-slate-400">Total for period</span>
                    <span className="text-xl font-bold text-white">
                      ${(transactions
                        .filter(t => t.status === 'settled')
                        .reduce((sum, t) => sum + (t.type === 'Refund' ? -t.amount_cents : t.amount_cents), 0) / 100
                      ).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Personal Information Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Personal Information
            </h3>
            <div className="space-y-4">
              {/* Primary Email (Read-only) */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  <Mail className="inline w-4 h-4 mr-2" />
                  Primary Email (User ID)
                </label>
                <div className="p-3 bg-slate-800/30 rounded-lg text-slate-400 border border-slate-700">
                  {user?.email}
                  <span className="ml-2 text-xs text-slate-500">(cannot be changed)</span>
                </div>
              </div>

              {/* Name Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    First Name
                  </label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    className="bg-slate-800/50 border-slate-700 text-slate-100"
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Last Name
                  </label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    className="bg-slate-800/50 border-slate-700 text-slate-100"
                    placeholder="Enter last name"
                  />
                </div>
              </div>

              {/* Contact Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    <Phone className="inline w-4 h-4 mr-2" />
                    Phone
                  </label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="bg-slate-800/50 border-slate-700 text-slate-100"
                    placeholder="Enter phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    <Mail className="inline w-4 h-4 mr-2" />
                    Alternate Email
                  </label>
                  <Input
                    type="email"
                    value={formData.alternate_email}
                    onChange={(e) => handleInputChange('alternate_email', e.target.value)}
                    className="bg-slate-800/50 border-slate-700 text-slate-100"
                    placeholder="Enter alternate email"
                  />
                </div>
              </div>

              {/* Location Fields */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  <MapPin className="inline w-4 h-4 mr-2" />
                  Location
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Input
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className="bg-slate-800/50 border-slate-700 text-slate-100"
                    placeholder="City"
                  />
                  <Input
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    className="bg-slate-800/50 border-slate-700 text-slate-100"
                    placeholder="State/Province"
                  />
                  <Input
                    value={formData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    className="bg-slate-800/50 border-slate-700 text-slate-100"
                    placeholder="Country"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Password Change Section */}
          <div className="pt-6 border-t border-slate-800">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Change Password
              </h3>
              {!changingPassword && (
                <Button
                  onClick={() => setChangingPassword(true)}
                  variant="outline"
                  size="sm"
                  className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
                >
                  Change Password
                </Button>
              )}
            </div>

            {changingPassword && (
              <div className="space-y-4">
                {/* Current Password */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <Input
                      type={showPasswords.current ? "text" : "password"}
                      value={passwordData.current_password}
                      onChange={(e) => handlePasswordChange('current_password', e.target.value)}
                      className="bg-slate-800/50 border-slate-700 text-slate-100 pr-10"
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('current')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                    >
                      {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <Input
                      type={showPasswords.new ? "text" : "password"}
                      value={passwordData.new_password}
                      onChange={(e) => handlePasswordChange('new_password', e.target.value)}
                      className="bg-slate-800/50 border-slate-700 text-slate-100 pr-10"
                      placeholder="Enter new password (min 8 characters)"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('new')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                    >
                      {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <Input
                      type={showPasswords.confirm ? "text" : "password"}
                      value={passwordData.confirm_password}
                      onChange={(e) => handlePasswordChange('confirm_password', e.target.value)}
                      className="bg-slate-800/50 border-slate-700 text-slate-100 pr-10"
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('confirm')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                    >
                      {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  onClick={() => {
                    setChangingPassword(false);
                    setPasswordData({
                      current_password: '',
                      new_password: '',
                      confirm_password: ''
                    });
                  }}
                  variant="outline"
                  size="sm"
                  className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
                >
                  Cancel Password Change
                </Button>
              </div>
            )}
          </div>

          {/* Roles Display */}
          {user?.roles && user.roles.length > 0 && (
            <div className="pt-6 border-t border-slate-800">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Account Roles
              </h3>
              <div className="flex flex-wrap gap-2">
                {user.roles.map(role => (
                  <span
                    key={role}
                    className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-indigo-500/20 text-indigo-300 ring-1 ring-indigo-400/40"
                  >
                    {role}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="pt-6 border-t border-slate-800 flex justify-end gap-3">
            <Button
              onClick={() => navigate(getHomePage(user))}
              variant="outline"
              className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAndClose}
              className="bg-indigo-600 text-white hover:bg-indigo-700"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save and Close'}
            </Button>
          </div>
        </div>
      </div>

      <UniversalFooter />
      
      {/* Pricing Modal for buying credits */}
      <PricingConversionModal />
      <PurchaseConfirmationModal />
    </div>
  );
};

export default AccountPage;
